"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Lqip } from "@/components/ui/Lqip";
import { useImageLoaded } from "@/lib/hooks/useImageLoaded";

type ProductCardImageSource = {
	href: string;
	alt?: string | null;
	/**
	 * Tiny Centra-generated placeholder URL rendered as a blurred backdrop
	 * while `next/image` loads the real source.
	 */
	lqip?: string;
};

type ProductCardImageProps = {
	firstImage?: ProductCardImageSource;
	secondImage?: ProductCardImageSource;
	productName?: string | null;
	size: "small" | "medium" | "large";
	children?: ReactNode;
	/**
	 * When true, the first (visible) image loads eagerly with `fetchPriority="high"`.
	 * Use for cards in the LCP region (first row of a PLP grid).
	 */
	priority?: boolean;
};

export const ProductCardImage = ({
	firstImage,
	secondImage,
	productName,
	size,
	children,
	priority = false,
}: ProductCardImageProps) => {
	const [isHovered, setIsHovered] = useState(false);

	// `*Loaded` flags drive the real `<Image>` opacity fade and unmount the
	// matching LQIP afterwards so the hover-swap fade doesn't bleed a
	// half-transparent placeholder through. `useImageLoaded` also covers the
	// cached-image case (callback ref checks `complete` on mount) so cached
	// `next/image` sources don't stick at opacity-0.
	const [firstLoaded, firstImgRef, onFirstImgLoad] = useImageLoaded();
	const [secondLoaded, secondImgRef, onSecondImgLoad] = useImageLoaded();

	return (
		<div
			className={twMerge(
				"relative w-full shrink-0 bg-background-bg-image overflow-hidden",
				size === "small" && "aspect-109/145",
				size === "medium" && "aspect-160/213",
				size === "large" && "aspect-3/4",
			)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={() => setIsHovered(false)}
			role="img"
			aria-label={productName || "Product image"}
		>
			{firstImage?.lqip && !firstLoaded && <Lqip src={firstImage.lqip} />}
			{firstImage && (
				<Image
					ref={firstImgRef}
					src={firstImage.href}
					alt={firstImage.alt || productName || ""}
					fill
					className={twMerge(
						"object-cover transition-opacity duration-300 ease-out",
						firstLoaded ? "opacity-100" : "opacity-0",
					)}
					sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
					priority={priority}
					fetchPriority={priority ? "high" : "auto"}
					onLoad={onFirstImgLoad}
				/>
			)}
			{secondImage?.lqip && !secondLoaded && <Lqip src={secondImage.lqip} visible={isHovered} />}
			{secondImage && (
				<Image
					ref={secondImgRef}
					src={secondImage.href}
					alt={secondImage.alt || productName || ""}
					fill
					className={twMerge(
						"object-cover transition-opacity duration-200 ease-out will-change-[opacity]",
						isHovered && secondLoaded ? "opacity-100" : "opacity-0 pointer-events-none",
					)}
					sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
					priority={false}
					onLoad={onSecondImgLoad}
				/>
			)}
			{children}
		</div>
	);
};
